import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { 
  Settings2, 
  Flag, 
  BarChart3, 
  TestTube2,
  Users,
  Activity,
  TrendingUp,
  Shield,
  Plus,
  Edit,
  Trash2,
  Toggle,
  Eye,
  EyeOff,
  Zap,
  Database,
  AlertCircle,
  CheckCircle,
  Clock,
  Percent
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { 
  FeatureCard, 
  ABTestCard, 
  FeatureDialog, 
  ConfigDialog, 
  ABTestDialog 
} from "./PlatformComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlatformManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [features, setFeatures] = useState([]);
  const [platformConfigs, setPlatformConfigs] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [featureAnalytics, setFeatureAnalytics] = useState(null);
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  
  // Dialog states
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showABTestDialog, setShowABTestDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [featureForm, setFeatureForm] = useState({
    name: '',
    display_name: '',
    description: '',
    category: 'general',
    is_active: true,
    required_tier: 'starter',
    rollout_percentage: 100.0,
    target_user_groups: [],
    usage_limit_per_month: null
  });
  
  const [configForm, setConfigForm] = useState({
    config_key: '',
    config_value: '',
    value_type: 'string',
    category: 'general',
    description: '',
    is_active: true
  });
  
  const [abTestForm, setAbTestForm] = useState({
    name: '',
    description: '',
    feature_name: '',
    variant_a: '',
    variant_b: '',
    traffic_split: 50.0,
    success_metric: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [featuresRes, configsRes, testsRes, featureAnalyticsRes, platformAnalyticsRes] = await Promise.all([
        axios.get(`${API}/admin/features`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/config`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ab-tests`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/analytics/features`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/analytics/platform`).catch(() => ({ data: null }))
      ]);
      
      setFeatures(featuresRes.data);
      setPlatformConfigs(configsRes.data);
      setAbTests(testsRes.data);
      setFeatureAnalytics(featureAnalyticsRes.data);
      setPlatformAnalytics(platformAnalyticsRes.data);
    } catch (error) {
      console.error("Error fetching platform data:", error);
      toast.error("Failed to load platform data");
    } finally {
      setLoading(false);
    }
  };

  // Feature Management Functions
  const createFeature = async () => {
    try {
      await axios.post(`${API}/admin/features`, featureForm);
      toast.success("Feature flag created successfully");
      setShowFeatureDialog(false);
      resetFeatureForm();
      fetchData();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create feature flag";
      toast.error(errorMessage);
    }
  };

  const updateFeature = async () => {
    try {
      await axios.put(`${API}/admin/features/${editingItem.id}`, featureForm);
      toast.success("Feature flag updated successfully");
      setShowFeatureDialog(false);
      resetFeatureForm();
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update feature flag");
    }
  };

  const deleteFeature = async (featureId, featureName) => {
    if (!window.confirm(`Are you sure you want to delete the "${featureName}" feature flag?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/features/${featureId}`);
      toast.success("Feature flag deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete feature flag");
    }
  };

  // Config Management Functions
  const createConfig = async () => {
    try {
      await axios.post(`${API}/admin/config`, configForm);
      toast.success("Configuration created successfully");
      setShowConfigDialog(false);
      resetConfigForm();
      fetchData();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create configuration";
      toast.error(errorMessage);
    }
  };

  const updateConfig = async () => {
    try {
      await axios.put(`${API}/admin/config/${editingItem.id}`, configForm);
      toast.success("Configuration updated successfully");
      setShowConfigDialog(false);
      resetConfigForm();
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update configuration");
    }
  };

  // A/B Test Management Functions
  const createABTest = async () => {
    try {
      await axios.post(`${API}/admin/ab-tests`, abTestForm);
      toast.success("A/B test created successfully");
      setShowABTestDialog(false);
      resetABTestForm();
      fetchData();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create A/B test";
      toast.error(errorMessage);
    }
  };

  // Form Reset Functions
  const resetFeatureForm = () => {
    setFeatureForm({
      name: '',
      display_name: '',
      description: '',
      category: 'general',
      is_active: true,
      required_tier: 'starter',
      rollout_percentage: 100.0,
      target_user_groups: [],
      usage_limit_per_month: null
    });
  };

  const resetConfigForm = () => {
    setConfigForm({
      config_key: '',
      config_value: '',
      value_type: 'string',
      category: 'general',
      description: '',
      is_active: true
    });
  };

  const resetABTestForm = () => {
    setAbTestForm({
      name: '',
      description: '',
      feature_name: '',
      variant_a: '',
      variant_b: '',
      traffic_split: 50.0,
      success_metric: ''
    });
  };

  const openEditFeature = (feature) => {
    setEditingItem(feature);
    setFeatureForm({ ...feature });
    setShowFeatureDialog(true);
  };

  const openEditConfig = (config) => {
    setEditingItem(config);
    setConfigForm({ ...config });
    setShowConfigDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Management</h1>
          <p className="text-gray-600">Manage platform features, configurations, and analytics</p>
        </div>
        <Badge className="bg-blue-100 text-blue-700">
          <Settings2 className="h-4 w-4 mr-1" />
          Platform Admin
        </Badge>
      </div>

      <Tabs defaultValue="analytics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="features">Feature Flags</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="abtests">A/B Testing</TabsTrigger>
        </TabsList>

        {/* Analytics Dashboard Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {platformAnalytics && (
              <>
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardDescription>Total Users</CardDescription>
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {platformAnalytics.total_users?.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardDescription>Active Features</CardDescription>
                      <Flag className="h-4 w-4 text-green-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {platformAnalytics.active_features}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardDescription>Business Plans</CardDescription>
                      <BarChart3 className="h-4 w-4 text-purple-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {platformAnalytics.total_business_plans?.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardDescription>Active Subscriptions</CardDescription>
                      <TrendingUp className="h-4 w-4 text-orange-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">
                      {platformAnalytics.active_subscriptions}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Feature Usage Analytics</CardTitle>
                <CardDescription>Most used features this month</CardDescription>
              </CardHeader>
              <CardContent>
                {featureAnalytics && featureAnalytics.feature_usage ? (
                  <div className="space-y-3">
                    {featureAnalytics.feature_usage.slice(0, 10).map((feature, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div>
                          <span className="text-sm font-medium">{feature.feature_name}</span>
                          <p className="text-xs text-gray-500">{feature.unique_users} users</p>
                        </div>
                        <Badge variant="outline">
                          {feature.total_usage} uses
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No feature usage data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subscription Distribution</CardTitle>
                <CardDescription>Users by subscription tier</CardDescription>
              </CardHeader>
              <CardContent>
                {platformAnalytics && platformAnalytics.subscription_distribution ? (
                  <div className="space-y-3">
                    {platformAnalytics.subscription_distribution.map((sub, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm font-medium capitalize">{sub._id}</span>
                        <Badge variant="outline">{sub.count} users</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No subscription data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Feature Flags Tab */}
        <TabsContent value="features" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Feature Flags Management</h2>
            <Button 
              onClick={() => setShowFeatureDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feature Flag
            </Button>
          </div>

          <div className="space-y-3">
            {features.map((feature) => (
              <FeatureCard 
                key={feature.id} 
                feature={feature} 
                onEdit={openEditFeature}
                onDelete={deleteFeature}
              />
            ))}
            
            {features.length === 0 && (
              <Card className="text-center py-8">
                <CardContent>
                  <Flag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No feature flags found</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowFeatureDialog(true)}
                  >
                    Create First Feature Flag
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Platform Configuration</h2>
            <Button 
              onClick={() => setShowConfigDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Configuration
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {platformConfigs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell className="font-medium">{config.config_key}</TableCell>
                  <TableCell className="max-w-xs truncate">{config.config_value}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {config.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="capitalize">{config.value_type}</TableCell>
                  <TableCell>
                    <Badge variant={config.is_active ? "default" : "secondary"}>
                      {config.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditConfig(config)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {platformConfigs.length === 0 && (
            <Card className="text-center py-8">
              <CardContent>
                <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No platform configurations found</p>
                <Button 
                  className="mt-4" 
                  onClick={() => setShowConfigDialog(true)}
                >
                  Create First Configuration
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* A/B Testing Tab */}
        <TabsContent value="abtests" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">A/B Testing</h2>
            <Button 
              onClick={() => setShowABTestDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create A/B Test
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {abTests.map((test) => (
              <ABTestCard key={test.id} test={test} />
            ))}
          </div>

          {abTests.length === 0 && (
            <Card className="text-center py-8">
              <CardContent>
                <TestTube2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No A/B tests found</p>
                <Button 
                  className="mt-4" 
                  onClick={() => setShowABTestDialog(true)}
                >
                  Create First A/B Test
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <FeatureDialog 
        open={showFeatureDialog}
        onClose={() => {
          setShowFeatureDialog(false);
          resetFeatureForm();
          setEditingItem(null);
        }}
        form={featureForm}
        setForm={setFeatureForm}
        onSubmit={editingItem ? updateFeature : createFeature}
        isEditing={!!editingItem}
      />

      <ConfigDialog 
        open={showConfigDialog}
        onClose={() => {
          setShowConfigDialog(false);
          resetConfigForm();
          setEditingItem(null);
        }}
        form={configForm}
        setForm={setConfigForm}
        onSubmit={editingItem ? updateConfig : createConfig}
        isEditing={!!editingItem}
      />

      <ABTestDialog 
        open={showABTestDialog}
        onClose={() => {
          setShowABTestDialog(false);
          resetABTestForm();
        }}
        form={abTestForm}
        setForm={setAbTestForm}
        onSubmit={createABTest}
        features={features}
      />
    </div>
  );
};

export default PlatformManager;